import { usePermissions } from './usePermissions'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { alertsApi, type AlertsParams } from '@/lib/api/alerts'
import type { ResolveAlertRequest } from '@/types'

const QUERY_KEYS = {
  alerts: (familyId: string, params?: AlertsParams) =>
    ['alerts', familyId, params] as const,
  alert: (familyId: string, alertId: string) =>
    ['alerts', familyId, alertId] as const,
  unreadCount: (familyId: string, memberId?: string) =>
    ['alerts', 'unread', familyId, memberId] as const,
}

export function useAlerts(familyId: string | null, params?: AlertsParams) {
  const queryClient = useQueryClient()
  const { isCaregiver } = usePermissions()

  const alertsQuery = useQuery({
    queryKey: QUERY_KEYS.alerts(familyId!, params),
    queryFn: () => alertsApi.getAlerts(familyId!, params),
    enabled: isCaregiver && !!familyId,
    staleTime: 1 * 60 * 1000, // 1 minute
  })

  const acknowledgeMutation = useMutation({
    mutationFn: ({ alertId, notes }: { alertId: string; notes?: string }) =>
      alertsApi.acknowledgeAlert(familyId!, alertId, { notes }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] })
    },
  })

  const resolveMutation = useMutation({
    mutationFn: ({ alertId, data }: { alertId: string; data: ResolveAlertRequest }) =>
      alertsApi.resolveAlert(familyId!, alertId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] })
    },
  })

  const escalateMutation = useMutation({
    mutationFn: ({ alertId, memberIds, message }: { alertId: string; memberIds: string[]; message: string }) =>
      alertsApi.escalateAlert(familyId!, alertId, { member_ids: memberIds, message }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] })
    },
  })

  return {
    alerts: isCaregiver ? alertsQuery.data?.data ?? [] : [],
    pagination: alertsQuery.data?.pagination,
    isLoading: alertsQuery.isLoading,
    error: alertsQuery.error,
    refetch: alertsQuery.refetch,

    acknowledgeAlert: acknowledgeMutation.mutateAsync,
    resolveAlert: resolveMutation.mutateAsync,
    escalateAlert: escalateMutation.mutateAsync,
    isAcknowledging: acknowledgeMutation.isPending,
    isResolving: resolveMutation.isPending,
    isEscalating: escalateMutation.isPending,
  }
}

export function useAlert(familyId: string | null, alertId: string | null) {
  const { isCaregiver } = usePermissions()
  return useQuery({
    queryKey: QUERY_KEYS.alert(familyId!, alertId!),
    queryFn: () => alertsApi.getAlert(familyId!, alertId!),
    enabled: isCaregiver && !!familyId && !!alertId,
    staleTime: 1 * 60 * 1000,
  })
}

export function useUnreadAlertCount(familyId: string | null, memberId?: string) {
  const { isCaregiver } = usePermissions()
  return useQuery({
    queryKey: QUERY_KEYS.unreadCount(familyId!, memberId),
    queryFn: () => alertsApi.getUnreadCount(familyId!, memberId),
    enabled: isCaregiver && !!familyId,
    staleTime: 30 * 1000, // 30 seconds - shorter for badge updates
  })
}
